'use client';

import clsx from 'clsx';
import { FocusTrap } from 'focus-trap-react';
import { ArrowLeft, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type JSX, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Button } from '../Button';
import { Portal } from '../Portal';
import { Spinner } from '../Spinner';
import styles from './Modal.module.scss';

interface ModalButtonProps {
  label?: string;
  title?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}

interface ModalProps {
  title: string;
  description?: string;
  centerHeader?: boolean;
  children?: React.ReactNode;
  isOpen: boolean;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  confirmButton?: ModalButtonProps;
  cancelButton?: ModalButtonProps;
  testId?: string;
  onClose?: () => void;
  onBack?: () => void;
}

export const Modal: React.FC<ModalProps> = ({
  title,
  description,
  centerHeader,
  children,
  isOpen,
  headingLevel = 2,
  confirmButton,
  cancelButton,
  testId,
  onClose,
  onBack,
}) => {
  const t = useTranslations('components.modal');
  const dialogRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const generatedId = useId();
  const titleId = `modal-title-${generatedId}`;

  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;

  // Covers the longest enter/exit defined in Modal.module.scss across
  // breakpoints (mobile sheet slides from --motion-duration-slow / -base,
  // desktop dialog uses --motion-duration-base / -fast).
  const transitionDuration = { appear: 240, enter: 240, exit: 200 };

  // Capture trigger element before FocusTrap moves focus (useLayoutEffect fires before useEffect)
  useLayoutEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <Portal>
      <CSSTransition
        in={isOpen}
        appear
        timeout={transitionDuration}
        unmountOnExit
        classNames={{
          appear: styles.modalAppear,
          appearActive: styles.modalAppearActive,
          enter: styles.modalEnter,
          enterActive: styles.modalEnterActive,
          exit: styles.modalExit,
          exitActive: styles.modalExitActive,
        }}
        nodeRef={modalRef}
      >
        {/*
          fallbackFocus points at the focusable dialog container so focus-trap
          does not throw "no tabbable node" — both when it activates while the
          enter transition still has the content hidden, and for content-only
          modals opened without any buttons.
        */}
        <FocusTrap
          active={isOpen}
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            fallbackFocus: `.${styles.dialog}`,
          }}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={styles.modal}
            data-testid={testId}
          >
            <div
              className={styles.backdrop}
              onClick={onClose}
              title={onClose ? t('close') : undefined}
              role={onClose ? 'button' : undefined}
              aria-hidden="true"
            >
              {onClose && <span className="sr-only">{t('clickBackdropToClose', { title })}</span>}
            </div>
            <div className={styles.dialog} tabIndex={-1} ref={dialogRef}>
              <header className={styles.header}>
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    title={t('backToPreviousStep')}
                    className={styles.headerButton}
                    data-testid={testId ? `${testId}-back-button` : undefined}
                  >
                    <span className="sr-only">{t('backToPreviousStep')}</span>
                    <ArrowLeft aria-hidden="true" />
                  </button>
                )}
                <div
                  className={clsx(styles.headerInfo, {
                    [styles.headerInfoCenter]: !!onBack || centerHeader,
                  })}
                >
                  <HeadingTag
                    id={titleId}
                    className={styles.title}
                    data-testid={testId ? `${testId}-title` : undefined}
                  >
                    {title}
                  </HeadingTag>
                  {description && (
                    <p
                      className={styles.description}
                      data-testid={testId ? `${testId}-description` : undefined}
                    >
                      {description}
                    </p>
                  )}
                </div>
                {onClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    title={t('closeModal', { title })}
                    className={styles.headerButton}
                    data-testid={testId ? `${testId}-close-button` : undefined}
                  >
                    <span className="sr-only">{t('closeModal', { title })}</span>
                    <X aria-hidden="true" />
                  </button>
                )}
              </header>
              {children && <div className={styles.body}>{children}</div>}
              {(confirmButton || cancelButton) && (
                <footer className={styles.footer}>
                  {cancelButton && (
                    <Button
                      variant="inverted"
                      onClick={cancelButton.onClick}
                      title={cancelButton.title}
                      disabled={cancelButton.disabled}
                      testId={cancelButton.testId}
                      data-testid={testId ? `${testId}-cancel-button` : undefined}
                    >
                      {cancelButton.label}
                      {cancelButton.loading && <Spinner size="text" />}
                    </Button>
                  )}
                  {confirmButton && (
                    <Button
                      variant="primary"
                      onClick={confirmButton.onClick}
                      title={confirmButton.title}
                      disabled={confirmButton.disabled}
                      testId={confirmButton.testId}
                      data-testid={testId ? `${testId}-confirm-button` : undefined}
                    >
                      {confirmButton.label}
                      {confirmButton.loading && <Spinner size="text" />}
                    </Button>
                  )}
                </footer>
              )}
            </div>
          </div>
        </FocusTrap>
      </CSSTransition>
    </Portal>
  );
};
